import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { auth } from '@yola/ws-sdk';
import { selectors } from '@yola/subscription-manager-js';
import { designSystem } from '@yola/ws-ui';
import registerHotkeys from 'src/js/modules/hotkeys/initializers';
import focalPoint from 'src/js/modules/focal-point';
import spacer from 'src/js/modules/spacer';
import EditorContainer from 'src/js/modules/editor/containers/editor-container';
import ContextMenuContainer from 'src/js/modules/context-menu/containers/context-menu-container';
import CustomUIContainer from 'src/js/modules/custom-ui/containers/custom-ui-container';
import WSDialogsContainer from 'src/js/modules/dialogs/containers/dialogs-container';
import StarterScreenContainer from 'yola-editor/src/js/modules/starter-screen/containers/starter-screen-container';
import YBotAuthContainer from 'yola-editor/src/js/components/containers/y-bot-auth-container.js';
import DialogsContainer from 'yola-editor/src/js/modules/dialogs/containers/dialogs-container';
import yolaAuth from 'yola-editor/src/js/modules/auth';
import user from 'yola-editor/src/js/modules/user';
import subscriptions from 'yola-editor/src/js/modules/subscriptions';
import site from 'yola-editor/src/js/modules/site';
import i18n from 'yola-editor/src/js/modules/i18n';
import config from 'src/js/modules/config';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import NetworkErrorHandler from 'src/js/modules/error/containers/network-error-handler';
import { FeatureFlagsProvider } from 'yola-editor/src/js/modules/feature-flags/context';
import starterScreen from 'yola-editor/src/js/modules/starter-screen';
import { registerContextMenuNavigationItems } from 'src/js/modules/navigation/initializers';
import { registerContextMenuBlockItems } from 'src/js/modules/blocks/initializers';
import ViewDropAreaContainer from 'src/js/modules/drag-n-drop/containers/view-drop-area-container';
import PreviewModeContainer from 'src/js/modules/preview-mode/containers/preview-mode-container';
import SiteUnderMaintenanceScreen from 'yola-editor/src/js/modules/maintenance/containers/site-under-maintenance-screen';
import { ViewportProvider } from 'yola-editor/src/js/utils/viewport-provider';
import cookieStoreChangeListener from 'yola-editor/src/js/cookies/listeners/cookie-store-change-listener';
import onYolaCookieChange from 'yola-editor/src/js/cookies/helpers/on-yola-cookie-change';
import onCookieDelete from 'yola-editor/src/js/cookies/helpers/on-cookie-delete';
import DifmPopupContainer from 'src/js/modules/difm/containers/difm-popup-container';

const { DragAndDropProvider } = designSystem;

const { SITE_ID_TO_RECREATE, TEMPLATE_BUILD_SLUG } = site.constants;
const LOADING_PROGRESS_INITIAL = 60;

function IndexRoute() {
  const token = useSelector(auth.selectors.getAuthToken);
  const partnerData = useSelector(user.selectors.getPartnerData);
  const userPreferences = useSelector(user.selectors.getUserPreferences);
  const userData = useSelector(user.selectors.getUserData);
  const userFeatures = useSelector(user.selectors.getUserFeatures);
  const areSubscriptionsLoaded = useSelector(subscriptions.selectors.getLoadingStatus);
  const isPublishedDataLoaded = useSelector(site.selectors.getPublishedDataLoadingStatus);
  const locale = useSelector(i18n.selectors.getLocale);
  const galleryLink = useSelector(config.selectors.getGalleryLink);
  const siteId = useSelector(yolaAuth.selectors.getSiteId);
  const siteData = useSelector(site.selectors.getSiteData);
  const templateBuildSlug = useSelector(site.selectors.getSiteTemplateSlug);
  const siteProperties = useSelector(site.selectors.getSiteProperties);
  const availablePackages = useSelector(selectors.getHostingPackages, shallowEqual);
  const currentPackage = useSelector(selectors.getCurrentPackage, shallowEqual);
  const yolaCookieName = useSelector(config.selectors.getYolaCookieName);

  const dispatch = useDispatch();
  const localeAvailable = Boolean(locale);

  useEffect(() => {
    registerHotkeys();
    focalPoint.initializers.initFocalPoint();
    spacer.initializers.initSpacer();
  }, []);

  useEffect(() => {
    if (localeAvailable) {
      // Context menu items should be translated properly, so we need to init them when locale is set
      registerContextMenuNavigationItems();
    }
  }, [localeAvailable]);

  useEffect(() => {
    if (localeAvailable) {
      registerContextMenuBlockItems();
    }
  }, [localeAvailable, templateBuildSlug]);

  useEffect(() => {
    dispatch(starterScreen.actions.setProgress({ progress: LOADING_PROGRESS_INITIAL }));
  }, [dispatch]);

  // We have to call cookieStoreChangeListener.add/remove only when userData is available
  // to handle the case when user logins via SSO (which changes the cookie to actual user data)
  useLayoutEffect(() => {
    if (userData?.id) {
      cookieStoreChangeListener.add([
        { name: yolaCookieName, onDelete: onCookieDelete, onChange: onYolaCookieChange },
      ]);
    }

    return () => {
      if (userData?.id) {
        cookieStoreChangeListener.remove();
      }
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [userData?.id]);

  if (
    !token ||
    !userPreferences ||
    !partnerData ||
    !userData ||
    !userFeatures ||
    !areSubscriptionsLoaded ||
    !isPublishedDataLoaded ||
    !locale ||
    !siteId ||
    !siteData ||
    !availablePackages ||
    !currentPackage
  ) {
    return null;
  }

  const isSiteReset = site.verifiers.isSiteReset(siteData);

  if (isSiteReset) {
    const buildSlugQuery = siteProperties.conceptSlug || templateBuildSlug;

    redirectToUrl(
      `${galleryLink}?${SITE_ID_TO_RECREATE}=${siteId}&${TEMPLATE_BUILD_SLUG}=${buildSlugQuery}`
    );
  }

  return (
    <ViewportProvider>
      <DragAndDropProvider>
        <FeatureFlagsProvider>
          <YBotAuthContainer />
          <EditorContainer />
          <ViewDropAreaContainer />
          <DialogsContainer />
          <WSDialogsContainer />
          <ContextMenuContainer />
          <CustomUIContainer />
          <StarterScreenContainer />
          <SiteUnderMaintenanceScreen />
          <NetworkErrorHandler />
          <PreviewModeContainer />
          <DifmPopupContainer />
        </FeatureFlagsProvider>
      </DragAndDropProvider>
    </ViewportProvider>
  );
}

export default IndexRoute;
